import { useEffect, useState } from "react";

import {
  useCustomFieldsAndGroups,
  useWorkspaceConnections,
} from "@web-app/api";
import {
  CurrencyInput,
  CustomDropdownField,
  CustomNumberField,
  CustomTextField,
  DateInput,
  GoogleMapAutoComplete,
  Input,
  Label,
  PhoneInput,
  SelectInput,
  TextArea,
} from "@web-app/components";
import {
  CustomField,
  CustomFieldValue,
  FieldValue,
  Order,
  OrderDeliveryType,
  OrderPriorityType,
  TransportType,
} from "@web-app/types";
import { isSameDate } from "@web-app/utils/time";

import {
  extractCustomFieldId,
  isCustomField,
  isSystemField,
} from "../../../../../settings/FormBuilderTab/constants";
import type {
  DetailsType,
  OrderFormField,
} from "../../../../../settings/FormBuilderTab/types";
import {
  getRequiredCustomFieldErrors,
  hasCustomFieldValue,
} from "../../../../CreateOrderDrawer/customFieldValidation";

import AddressDetailsAccordion from "./AddressDetailsAccordion";
import DateChangeModal from "./DateChangeModal";
import EditItemChecklist from "./EditItemChecklist";

const SKIP_IN_EDIT = new Set([
  "systemField_timezone",
  "systemField_disableSMS",
  "systemField_saveDetails",
]);

const priorityOptions = [
  { label: "Low", value: OrderPriorityType.LOW },
  { label: "High", value: OrderPriorityType.HIGH },
];

interface EditConfigDrivenTabProps {
  debouncedHandleFieldChange: (property: string, value: FieldValue) => void;
  detailsType: DetailsType;
  fields: OrderFormField[];
  onFieldChange: (
    property: string,
    value: FieldValue,
    fromTab?: "dropoff" | "pickup",
  ) => void;
  order: Order;
}

export default function EditConfigDrivenTab({
  debouncedHandleFieldChange,
  detailsType,
  fields,
  onFieldChange,
  order,
}: EditConfigDrivenTabProps) {
  const isPickup = detailsType === "pickup";
  const addressType: "dropoff" | "pickup" = isPickup ? "pickup" : "dropoff";
  const isP2PDelivery =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const [{ data: customFields }] = useCustomFieldsAndGroups();
  const { data: workspaceConnections } = useWorkspaceConnections();

  const [localValue, setLocalValue] = useState(
    String(order.details.value || ""),
  );
  const [localTip, setLocalTip] = useState(String(order.details.tip || ""));
  const [newStartDate, setNewStartDate] = useState<Date | null>(null);
  const [customFieldErrors, setCustomFieldErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setLocalValue(String(order.details.value || ""));
  }, [order.details.value]);

  useEffect(() => {
    setLocalTip(String(order.details.tip || ""));
  }, [order.details.tip]);

  // Surface required custom fields that are already empty when the panel opens,
  // rather than waiting for the user to touch them. Errors already in state (a
  // field the user just cleared, which is never saved) win over the seeded ones.
  useEffect(() => {
    if (!customFields) return;
    // Mirror what the inputs render: the order's values, falling back to the
    // definition's defaults.
    const renderedState = customFields.map((definition) => {
      const values = order.customFields?.find(
        (cf) => cf.definition.id === definition.id,
      )?.values;
      return {
        definition,
        values: values?.length ? values : (definition.defaultValues ?? []),
      };
    });
    const seeded = getRequiredCustomFieldErrors(
      fields,
      customFields,
      renderedState,
    );
    setCustomFieldErrors((prev) => {
      const next = { ...seeded, ...prev };
      const isUnchanged =
        Object.keys(next).length === Object.keys(prev).length &&
        Object.keys(next).every((id) => next[id] === prev[id]);
      return isUnchanged ? prev : next;
    });
  }, [customFields, fields, order.customFields]);

  const sortedFields = [...fields].sort((a, b) => a.position - b.position);

  const handleCustomFieldChange = (
    customField: CustomField,
    value: CustomFieldValue[],
    isRequired: boolean,
  ) => {
    if (isRequired && !hasCustomFieldValue(customField, value)) {
      setCustomFieldErrors((prev) => ({
        ...prev,
        [customField.id]: `${customField.name} is required`,
      }));
      return;
    }
    setCustomFieldErrors((prev) => {
      if (!prev[customField.id]) return prev;
      const next = { ...prev };
      delete next[customField.id];
      return next;
    });

    const existingIndex = (order.customFields ?? []).findIndex(
      (cf) => cf.definition.id === customField.id,
    );
    const updated = [...(order.customFields ?? [])];
    if (existingIndex > -1) {
      updated[existingIndex] = { definition: customField, values: value };
    } else {
      updated.push({ definition: customField, values: value });
    }
    debouncedHandleFieldChange("customFields", updated);
  };

  // The field components call onChange with (fieldId, customField, values,
  // groupId). Spelling the signature out here keeps TypeScript checking the
  // shape instead of relying on the inline arrow silently matching.
  const makeCustomFieldChangeHandler =
    (isRequired: boolean) =>
    (_fieldId: string, customField: CustomField, values: CustomFieldValue[]) =>
      handleCustomFieldChange(customField, values, isRequired);

  const onStartDateChange = (date: Date) => {
    onFieldChange(
      "scheduledStartDateTime",
      date.toISOString(),
      isPickup ? "pickup" : "dropoff",
    );
  };

  // --- Pickup / Dropoff field renderers ---

  const renderFullNameField = (field: OrderFormField) => (
    <div className="flex flex-col gap-1">
      <Label required={field.isRequired}>Full Name</Label>
      <Input
        defaultValue={
          (isPickup ? order.pickup?.name : order.dropoff?.name) || ""
        }
        onChange={(value: string) =>
          debouncedHandleFieldChange(`${addressType}.name`, value)
        }
        placeholder={`Enter ${isPickup ? "pickup" : "drop-off"} name`}
      />
    </div>
  );

  const renderPhoneField = (field: OrderFormField) => (
    <div className="flex flex-col gap-1">
      <Label required={field.isRequired}>Phone Number</Label>
      <PhoneInput
        defaultValue={
          (isPickup ? order.pickup?.phone : order.dropoff?.phone) || ""
        }
        onChange={(value: string) =>
          debouncedHandleFieldChange(`${addressType}.phone`, value)
        }
      />
    </div>
  );

  const renderAddressField = (field: OrderFormField) => (
    <div className="flex flex-col gap-0">
      <div className="flex flex-col gap-1">
        <Label required={field.isRequired}>Address</Label>
        <GoogleMapAutoComplete
          // @ts-ignore - address is not a required field
          address={{
            description:
              (isPickup ? order.pickup?.address : order.dropoff?.address) || "",
          }}
          setAddress={async (address) => {
            await onFieldChange(`${addressType}.address`, address);
          }}
          placeholder={`Enter ${isPickup ? "pickup" : "dropoff"} location`}
        />
      </div>
      <AddressDetailsAccordion
        addressType={addressType}
        debouncedHandleFieldChange={debouncedHandleFieldChange}
        location={isPickup ? order.pickup : order.dropoff}
      />
    </div>
  );

  const renderPickupAtField = () => (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <Label required>
            {isP2PDelivery
              ? "Pickup At (Start Time)"
              : "Pickup After (Date/Time)"}
          </Label>
          <DateInput
            value={new Date(order.scheduledStartDateTime)}
            onChange={(date) => {
              if (isSameDate(date, order.scheduledStartDateTime)) {
                onStartDateChange(date);
              } else {
                setNewStartDate(date);
              }
            }}
            timezone={order.timezone}
            position="under"
          />
        </div>
        {!isP2PDelivery && (
          <div className="flex flex-col gap-1">
            <Label required>Pickup Before (Date/Time)</Label>
            <DateInput
              value={new Date(order.scheduledEndDateTime)}
              onChange={(date) =>
                onFieldChange(
                  "scheduledEndDateTime",
                  date.toISOString(),
                  "pickup",
                )
              }
              timezone={order.timezone}
              position="under"
            />
          </div>
        )}
      </div>
      {newStartDate && (
        <DateChangeModal
          startDate={newStartDate}
          orderTimezone={order.timezone}
          onDateChangeCancel={() => setNewStartDate(null)}
          onDateChangeConfirm={() => {
            onStartDateChange(newStartDate);
            setNewStartDate(null);
          }}
        />
      )}
    </>
  );

  const renderDropoffAtField = () => (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {!isP2PDelivery && (
          <div className="flex flex-col gap-1">
            <Label required>Drop-off After (Date/Time)</Label>
            <DateInput
              value={new Date(order.scheduledStartDateTime)}
              onChange={(date) => {
                if (isSameDate(date, order.scheduledStartDateTime)) {
                  onStartDateChange(date);
                } else {
                  setNewStartDate(date);
                }
              }}
              timezone={order.timezone}
              position="under"
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label required>
            {isP2PDelivery
              ? "Drop-off At (End Time)"
              : "Drop-off Before (Date/Time)"}
          </Label>
          <DateInput
            value={new Date(order.scheduledEndDateTime)}
            onChange={(date) =>
              onFieldChange(
                "scheduledEndDateTime",
                date.toISOString(),
                "dropoff",
              )
            }
            timezone={order.timezone}
            position="under"
          />
        </div>
      </div>
      {newStartDate && (
        <DateChangeModal
          startDate={newStartDate}
          orderTimezone={order.timezone}
          onDateChangeCancel={() => setNewStartDate(null)}
          onDateChangeConfirm={() => {
            onStartDateChange(newStartDate);
            setNewStartDate(null);
          }}
        />
      )}
    </>
  );

  const renderBusinessNameField = () => (
    <div className="flex flex-col gap-1">
      <Label>Business Name</Label>
      <Input
        defaultValue={
          (isPickup
            ? order.pickup?.businessName
            : order.dropoff?.businessName) || ""
        }
        onChange={(value: string) =>
          debouncedHandleFieldChange(`${addressType}.businessName`, value)
        }
        placeholder="Enter business name"
      />
    </div>
  );

  const renderNoteField = () => (
    <div className="flex flex-col gap-1">
      <Label>Note</Label>
      <TextArea
        placeholder={`Enter ${isPickup ? "pickup" : "drop-off"} note`}
        rows={3}
        value={isPickup ? order.pickup.note : order.dropoff.note}
        onConfirm={(value) => {
          onFieldChange(`${addressType}.note`, value);
        }}
      />
    </div>
  );

  const renderOrderRequirementsField = () => {
    const prefix = isPickup ? "pickupRequirements" : "dropoffRequirements";
    const requirements = isPickup
      ? order.pickupRequirements
      : order.dropoffRequirements;

    return (
      <div className="flex flex-col gap-1">
        <Label>Order Requirements</Label>
        <Input
          type="checkbox"
          rightLabel="Signature Required"
          value={requirements.signature ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange(`${prefix}.signature`, value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Photo Required"
          value={requirements.photo ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange(`${prefix}.photo`, value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Notes Required"
          value={requirements.note ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange(`${prefix}.note`, value === "true")
          }
        />
        <Input
          type="checkbox"
          rightLabel="Item Checklist Required"
          value={requirements.itemChecklist ? "true" : "false"}
          onChange={(value: string) =>
            onFieldChange(`${prefix}.itemChecklist`, value === "true")
          }
        />
      </div>
    );
  };

  // --- Order tab renderers ---

  const renderDescriptionField = () => (
    <div className="flex flex-col gap-1">
      <Label>Description</Label>
      <TextArea
        value={order.details.description || ""}
        rows={3}
        onConfirm={(value) => onFieldChange("details.description", value ?? "")}
        placeholder="Enter order description"
      />
    </div>
  );

  const renderExternalIdField = () => (
    <div className="flex flex-col gap-1">
      <Label>External Reference ID</Label>
      <Input
        defaultValue={order.externalId || ""}
        onChange={(value: string) =>
          debouncedHandleFieldChange("externalId", value)
        }
        placeholder="Enter reference ID"
      />
    </div>
  );

  const renderValueField = () => (
    <div className="flex flex-col gap-1">
      <Label>Value</Label>
      <CurrencyInput
        value={localValue}
        onChange={(value: string) => {
          setLocalValue(value);
          debouncedHandleFieldChange("details.value", parseFloat(value) || 0);
        }}
        selectedCurrency={order.details.currency || "USD"}
        onCurrencyChange={(currency: string) =>
          onFieldChange("details.currency", currency)
        }
        placeholder="Enter order value"
      />
    </div>
  );

  const renderTipField = () => (
    <div className="flex flex-col gap-1">
      <Label>Tip</Label>
      <CurrencyInput
        value={localTip}
        onChange={(value: string) => {
          setLocalTip(value);
          debouncedHandleFieldChange("details.tip", parseFloat(value) || 0);
        }}
        selectedCurrency={order.details.currency || "USD"}
        onCurrencyChange={(currency: string) =>
          onFieldChange("details.currency", currency)
        }
        placeholder="Enter tip value"
      />
    </div>
  );

  const renderItemsCountField = () => (
    <div className="flex flex-col gap-1">
      <Label>Items Count</Label>
      <Input
        type="number"
        defaultValue={order.details.itemsCount || ""}
        onChange={(value: string) =>
          debouncedHandleFieldChange("details.itemsCount", parseInt(value) || 0)
        }
        placeholder="Enter number of items"
      />
    </div>
  );

  const renderServiceTimeField = (field: OrderFormField) => (
    <div className="flex flex-col gap-1">
      <Label required={field.isRequired}>Service Time (minutes)</Label>
      <Input
        type="number"
        min={0}
        step={1}
        defaultValue={order.details.serviceDuration ?? 0}
        onChange={(value: string) => {
          const duration = Number(value);
          debouncedHandleFieldChange(
            "details.serviceDuration",
            Math.max(0, Number.isFinite(duration) ? Math.round(duration) : 0),
          );
        }}
        placeholder="Enter service time"
      />
    </div>
  );

  const renderDimensionField = (
    propPath: string,
    label: string,
    currentValue: number | string | undefined,
  ) => (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input
        type="number"
        defaultValue={currentValue || "0.00"}
        onChange={(value: string) =>
          debouncedHandleFieldChange(propPath, parseFloat(value) || 0)
        }
        placeholder={`Enter ${label.toLowerCase()}`}
      />
    </div>
  );

  const renderVehicleTypeField = () => (
    <div className="flex flex-col gap-1">
      <Label>Vehicle Type</Label>
      <SelectInput
        options={Object.entries(TransportType).map(([key, value]) => ({
          label: key,
          value: value,
        }))}
        selected={order.minVehicle || ""}
        onChange={(value: string) => onFieldChange("minVehicle", value)}
        placeholder="Enter vehicle type"
      />
    </div>
  );

  const renderPriorityField = () => (
    <div className="flex flex-col gap-1">
      <Label>Priority</Label>
      <SelectInput
        options={priorityOptions}
        selected={order.priority || OrderPriorityType.LOW}
        onChange={(value: string) => onFieldChange("priority", value)}
        placeholder="Select priority"
      />
    </div>
  );

  const renderConnectedWorkspaceField = () => {
    const workspaceOptions = [
      { label: "None", value: "" },
      ...(workspaceConnections?.map((connection) => ({
        label: connection.connectedWorkspace.name,
        value: connection.connectedWorkspace.id,
      })) ?? []),
    ];

    return (
      <div className="flex flex-col gap-1">
        <Label>Connected Workspace</Label>
        <SelectInput
          options={workspaceOptions}
          selected={order.connectedWorkspaceId || ""}
          onChange={(value: string) =>
            onFieldChange("connectedWorkspaceId", value)
          }
          placeholder="Select a workspace"
        />
      </div>
    );
  };

  // The checklist belongs to a leg. Configs saved with it under "order" are
  // moved to the legs by normalizeItemChecklistFields.
  const renderItemChecklistField = () =>
    detailsType === "order" ? null : (
      <EditItemChecklist
        checklistKey={isPickup ? "pickupChecklist" : "dropoffChecklist"}
        order={order}
        onFieldChange={onFieldChange}
        debouncedHandleFieldChange={debouncedHandleFieldChange}
      />
    );

  // --- Custom field renderer ---

  const renderCustomFieldInline = (field: OrderFormField) => {
    const cfId = extractCustomFieldId(field.fieldKey);
    const cfDef = customFields?.find((cf) => cf.id === cfId);
    if (!cfDef) return null;

    const stateEntry = order.customFields?.find(
      (s) => s.definition.id === cfId,
    );
    const fieldValues =
      stateEntry?.values && stateEntry.values.length > 0
        ? stateEntry.values
        : (cfDef.defaultValues ?? []);

    switch (cfDef.type) {
      case "TEXT":
        return (
          <CustomTextField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={makeCustomFieldChangeHandler(field.isRequired)}
            isRequired={field.isRequired}
            error={customFieldErrors[cfId]}
          />
        );
      case "NUMBER":
        return (
          <CustomNumberField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={makeCustomFieldChangeHandler(field.isRequired)}
            isRequired={field.isRequired}
            error={customFieldErrors[cfId]}
          />
        );
      case "DROPDOWN":
        return (
          <CustomDropdownField
            fieldId={`field-id-${cfDef.name}`}
            customField={cfDef}
            fieldValues={fieldValues}
            isReadOnly={false}
            onChange={makeCustomFieldChangeHandler(field.isRequired)}
            isRequired={field.isRequired}
            error={customFieldErrors[cfId]}
          />
        );
      default:
        return null;
    }
  };

  // --- Field dispatch ---

  const renderSystemField = (field: OrderFormField) => {
    switch (field.fieldKey) {
      case "systemField_fullName":
        return renderFullNameField(field);
      case "systemField_phone":
        return renderPhoneField(field);
      case "systemField_address":
        return renderAddressField(field);
      case "systemField_pickupAt":
        return renderPickupAtField();
      case "systemField_dropoffAt":
        return renderDropoffAtField();
      case "systemField_businessName":
        return renderBusinessNameField();
      case "systemField_note":
        return renderNoteField();
      case "systemField_orderRequirements":
        return renderOrderRequirementsField();
      case "systemField_description":
        return renderDescriptionField();
      case "systemField_externalId":
        return renderExternalIdField();
      case "systemField_vehicleType":
        return renderVehicleTypeField();
      case "systemField_value":
        return renderValueField();
      case "systemField_tip":
        return renderTipField();
      case "systemField_itemsCount":
        return renderItemsCountField();
      case "systemField_serviceTime":
        return renderServiceTimeField(field);
      case "systemField_length":
        return renderDimensionField(
          "details.length",
          "Length (cm)",
          order.details.length,
        );
      case "systemField_width":
        return renderDimensionField(
          "details.width",
          "Width (cm)",
          order.details.width,
        );
      case "systemField_height":
        return renderDimensionField(
          "details.height",
          "Height (cm)",
          order.details.height,
        );
      case "systemField_weight":
        return renderDimensionField(
          "details.weight",
          "Weight (gm)",
          order.details.weight,
        );
      case "systemField_priority":
        return renderPriorityField();
      case "systemField_connectedWorkspaceId":
        return renderConnectedWorkspaceField();
      case "systemField_itemChecklist":
        return renderItemChecklistField();
      default:
        return null;
    }
  };

  const renderField = (field: OrderFormField) => {
    if (SKIP_IN_EDIT.has(field.fieldKey)) return null;
    if (isSystemField(field.fieldKey)) return renderSystemField(field);
    if (isCustomField(field.fieldKey)) return renderCustomFieldInline(field);
    return null;
  };

  return (
    <div className="px-4 pb-10 lg:pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        {sortedFields.map((field) => {
          const content = renderField(field);
          if (!content) return null;
          return (
            <div
              key={`${field.fieldKey}-${field.position}`}
              className={
                field.colSpan === 2 ? "col-span-1 lg:col-span-2" : "col-span-1"
              }
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
